import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Calendar, Layers, ShieldCheck, GitMerge, Target, Clock,
  Zap, LineChart, ClipboardCheck,
} from "lucide-react";
import { LegalHeader, LegalFooter } from "./legal/Terms";
import useSEO from "../lib/useSEO";
import { bookingUrl, isExternalBooking } from "../lib/bookingUrl";

const ASSESSMENT_URL = bookingUrl("transformation-assessment");
const OPEN_IN_NEW_TAB = isExternalBooking();

const DOMAINS = [
  { icon: Target,          label: "Fit-to-Standard",        body: "Workshop-driven gap analysis, process fit scoring, deviation register." },
  { icon: GitMerge,        label: "Data Migration",         body: "Object catalogue, cleansing scorecards, mock-load cycles, reconciliation reports." },
  { icon: Zap,             label: "Integration",            body: "Interface register with owner, protocol, middleware and test status." },
  { icon: ClipboardCheck,  label: "Testing",                body: "SIT / UAT / performance / regression cycles, defect triage, evidence packs." },
  { icon: Layers,          label: "OCM & Training",         body: "Change-impact heatmap, stakeholder journey mapping, adoption metrics." },
  { icon: Clock,           label: "Cutover & Hypercare",    body: "Minute-by-minute runbook, dress rehearsals, hypercare command centre." },
  { icon: LineChart,       label: "Value Realization",      body: "Planned vs achieved benefits, benefit owners, board-ready value dashboards." },
  { icon: ShieldCheck,     label: "Executive Governance",   body: "Steering committees, RACI matrices, decision logs, escalation ladders, audit trails." },
];

const OUTCOMES = [
  "Consolidate every workstream into one governance view",
  "Wire in existing SAP / D365 / Oracle tenants read-only for live cockpits",
  "Give the sponsor a board-ready update in the time it takes to open the app",
  "Replace hand-built RAG spreadsheets with a diagnostics engine that flags red the moment it appears",
  "Keep your integrator's methodology — Capittance is the delivery OS, not a replacement for the SI",
];

export default function EnterprisePage() {
  useSEO({
    title: "Enterprise Transformation Edition · Capittance Harmonizer",
    description: "Control complex SAP, D365 and Oracle transformations without replacing every delivery tool. Fit-to-standard, data migration, integration, testing, OCM, cutover, hypercare and value realization — one intelligent portal.",
    path: "/enterprise",
  });
  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="Enterprise Transformation Edition" subtitle="Control complex transformations without replacing every delivery tool." />

      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="page-enterprise">
        {/* Hero */}
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#7A1F2B]">
            For ERP transformation leaders · Enterprise PMOs · Advisory / SI partners
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-900 mt-3 leading-[1.05]">
            Govern the whole transformation from
            <span className="text-[#7A1F2B]"> one intelligent portal.</span>
          </h1>
          <p className="text-base text-zinc-600 mt-4 max-w-2xl mx-auto leading-relaxed">
            Capittance Harmonizer connects your delivery workstreams, executive governance and program controls into one live workspace —
            without displacing your system integrator, your PMO templates, or your ERP toolchain.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            <a
              href={ASSESSMENT_URL}
              {...(OPEN_IN_NEW_TAB ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="btn btn-primary inline-flex items-center gap-2"
              data-testid="enterprise-request-assessment"
            >
              <Calendar size={14}/> Request a Transformation Assessment <ArrowRight size={13}/>
            </a>
            <Link to="/pricing" className="btn btn-ghost btn-sm" data-testid="enterprise-see-pricing">
              See team &amp; executive pricing
            </Link>
          </div>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            30-min scoping call · your transformation lead + our founder · no sales script
          </div>
        </div>

        {/* Who it is for */}
        <section className="mt-14" data-testid="enterprise-audience">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1">Built for</div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4">Enterprise transformation leaders</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "ERP transformation directors leading SAP S/4HANA, D365 or Oracle programmes",
              "Enterprise PMO leaders governing a portfolio of transformation initiatives",
              "System integrators & advisory firms delivering multi-workstream programmes for clients",
              "CIOs sponsoring a multi-year transformation and needing one truth for the board",
            ].map((t, i) => (
              <div key={i} className="card p-4 text-sm text-zinc-700 leading-relaxed">
                <span className="text-[#7A1F2B] font-bold mr-2">▸</span>{t}
              </div>
            ))}
          </div>
        </section>

        {/* Domains covered */}
        <section className="mt-14" data-testid="enterprise-domains">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1">Coverage</div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4">Every workstream, one governance layer</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {DOMAINS.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.label} className="card p-4" data-testid={`enterprise-domain-${d.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-sm grid place-items-center text-white shrink-0" style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 100%)" }}>
                      <Icon size={14} strokeWidth={2.2}/>
                    </span>
                    <div>
                      <div className="font-display text-base font-bold tracking-tight">{d.label}</div>
                      <div className="text-sm text-zinc-600 leading-relaxed mt-0.5">{d.body}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* What the assessment produces */}
        <section className="mt-14" data-testid="enterprise-outcomes">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1">What the assessment call produces</div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4">A one-page transformation-control plan</h2>
          <ul className="space-y-2">
            {OUTCOMES.map((o, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-700 leading-relaxed">
                <span className="mt-0.5 text-[#7A1F2B] font-bold">▸</span>
                <span>{o}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 p-4 rounded-sm border border-[var(--border)] bg-zinc-50 text-xs text-zinc-600 leading-relaxed">
            Custom commercial terms available for the first cohort — annual licensing, guided onboarding, SSO, security review, integration support and dedicated success touchpoints. No packaged bundle; each deployment is scoped to the programme.
          </div>
        </section>

        {/* Final CTA */}
        <section className="mt-14 mb-6 text-center" data-testid="enterprise-final-cta">
          <h3 className="font-display text-2xl font-extrabold tracking-tight text-zinc-900">Bring your transformation into one view.</h3>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl mx-auto">
            The Request-a-Transformation-Assessment call is a 30-minute working session — no slide deck, no pitch.
            You walk out with a one-page control plan for your programme.
          </p>
          <a
            href={ASSESSMENT_URL}
            {...(OPEN_IN_NEW_TAB ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            className="btn btn-primary inline-flex items-center gap-2 mt-5"
            data-testid="enterprise-request-assessment-bottom"
          >
            <Calendar size={14}/> Request a Transformation Assessment <ArrowRight size={13}/>
          </a>
        </section>

        <LegalFooter />
      </main>
    </div>
  );
}
